// @ts-nocheck
import sleep from "@insite/client-framework/Common/Sleep";
import { Dictionary } from "@insite/client-framework/Common/Types";
import { addToChainAfter, makeHandlerChainAwaitable, replaceInChain } from "@insite/client-framework/HandlerCreator";
import { sendAutocompleteEvent } from "@insite/client-framework/OdpEvents/AutocompleteEvent";
import { createEvent, sendEvent } from "@insite/client-framework/OdpEvents/OdpEvent";
import { sendAddToCartEvent, sendRemoveFromCartEvent } from "@insite/client-framework/OdpEvents/UpdateCartEvents";
import { autocompleteSearch } from "@insite/client-framework/Services/AutocompleteService";
import { Category, getCategoryById } from "@insite/client-framework/Services/CategoryService";
import {
    chain as clearProductChain,
    DispatchCompleteSetProduct as ClearProductDispatchCompleteSetProduct,
} from "@insite/client-framework/Store/Components/ProductSelector/Handlers/ClearProduct";
import {
    chain as searchProductsChain,
    DispatchCompleteSearchProducts,
    RequestDataFromApi as SearchProductsRequestDataFromApi,
} from "@insite/client-framework/Store/Components/ProductSelector/Handlers/SearchProducts";
import {
    chain as setProductChain,
    DispatchBeginSetProduct,
} from "@insite/client-framework/Store/Components/ProductSelector/Handlers/SetProduct";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import {
    getMergedAttributeTypeFacets,
    getProductsDataView,
} from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import {
    chain as addLinesToCartChain,
    LoadCart as AddLinesToCartLoadCart,
} from "@insite/client-framework/Store/Pages/Cart/Handlers/AddLinesToCart";
import {
    chain as addWishListToCartChain,
    LoadCart as AddWishListToCartLoadCart,
} from "@insite/client-framework/Store/Pages/Cart/Handlers/AddWishListToCart";
import {
    CallClearCart as ClearCurrentCartCallClearCart,
    chain as clearCurrentCartChain,
} from "@insite/client-framework/Store/Pages/Cart/Handlers/ClearCurrentCart";
import {
    chain as orderDetailsReorderChain,
    LoadCart as OrderDetailsReorderLoadCart,
} from "@insite/client-framework/Store/Pages/OrderDetails/Handlers/Reorder";
import {
    chain as orderHistoryReorderChain,
    LoadCart as OrderHistoryReorderLoadCart,
} from "@insite/client-framework/Store/Pages/OrderHistory/Handlers/Reorder";
import {
    chain as orderStatusReorderChain,
    LoadCart as OrderStatusReorderLoadCart,
} from "@insite/client-framework/Store/Pages/OrderStatus/Handlers/Reorder";
import {
    chain as orderUploadProductsChain,
    SendDataToApi as OrderUploadProductsSendDataToApi,
} from "@insite/client-framework/Store/Pages/OrderUpload/Handlers/AddCartLineCollectionFromProducts";
import {
    chain as orderUploadVmiBinsChain,
    SendDataToApi as OrderUploadVmiBinsSendDataToApi,
} from "@insite/client-framework/Store/Pages/OrderUpload/Handlers/AddCartLineCollectionFromVmiBins";
import {
    chain as sendProductListOdpEventsChain,
    SendOdpEvents as ProductListSendOdpEvents,
} from "@insite/client-framework/Store/Pages/ProductList/Handlers/SendOdpEvents";
import { sendCategoryPageViewEvent } from "@insite/client-framework/OdpEvents/CategoryPageViewEvent";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import {
    chain as requisitionsAddLinesToCartChain,
    RequestDataFromApi as RequisitionsRequestDataFromApi,
} from "@insite/client-framework/Store/Pages/Requisitions/Handlers/AddLinesToCart";
import {
    chain as savedOrderPlaceOrderChain,
    SendDataToApi as SavedOrderSendDataToApi,
} from "@insite/client-framework/Store/Pages/SavedOrderDetails/Handlers/PlaceOrder";
import cloneDeep from "lodash/cloneDeep";
import isEqual from "lodash/isEqual";

// 1. AddWishListToCart: await loadCurrentCart then emit sendAddToCartEvent for each added line
replaceInChain(addWishListToCartChain, AddWishListToCartLoadCart, async props => {
    /* Store Custom Code - Start */
    // Base LoadCart only called `props.dispatch(loadCurrentCart({ getPromotions: true }))` without awaiting.
    const awaitableLoadCurrentCart = makeHandlerChainAwaitable(loadCurrentCart);
    await awaitableLoadCurrentCart({ getPromotions: true })(props.dispatch, props.getState);
    const state = props.getState();
    props.apiResult?.cartLines?.forEach(line => {
        if (line.productId && line.qtyOrdered) {
            sendAddToCartEvent({ productId: line.productId, qtyOrdered: line.qtyOrdered }, state);
        }
    });
    /* Store Custom Code - End */
});

// 2. AddLinesToCart: await loadCurrentCart then emit sendAddToCartEvent for each added line
replaceInChain(addLinesToCartChain, AddLinesToCartLoadCart, async props => {
    /* Store Custom Code - Start */
    // Base LoadCart only called `props.dispatch(loadCurrentCart({ getPromotions: true }))` without awaiting.
    const awaitableLoadCurrentCart = makeHandlerChainAwaitable(loadCurrentCart);
    await awaitableLoadCurrentCart({ getPromotions: true })(props.dispatch, props.getState);
    const state = props.getState();
    const lines = props.apiParameter.cartLineCollection?.cartLines ?? props.apiResult?.cartLines;
    lines?.forEach(line => {
        if (line.productId && line.qtyOrdered) {
            sendAddToCartEvent({ productId: line.productId, qtyOrdered: line.qtyOrdered }, state);
        }
    });
    /* Store Custom Code - End */
});

// 3. ClearCurrentCart: emit sendRemoveFromCartEvent before clearing current cart
replaceInChain(clearCurrentCartChain, ClearCurrentCartCallClearCart, async props => {
    /* Store Custom Code - Start */
    const state = props.getState();
    const cart = getCurrentCartState(state).value;
    cart?.cartLines?.forEach(line => {
        if (line.productId && line.qtyOrdered) {
            sendRemoveFromCartEvent({ productId: line.productId, qtyOrdered: line.qtyOrdered }, state);
        }
    });
    /* Store Custom Code - End */
    await ClearCurrentCartCallClearCart(props);
});

// 4. OrderDetails / OrderHistory / OrderStatus Reorder
const emitReorderAddToCart = (props: any) => {
    const state = props.getState();
    const lines = props.apiParameter?.cartLines ?? props.apiResult?.cartLines;
    lines?.forEach((line: any) => {
        if (line.productId && line.qtyOrdered) {
            sendAddToCartEvent({ productId: line.productId, qtyOrdered: line.qtyOrdered }, state);
        }
    });
};
addToChainAfter(orderDetailsReorderChain, OrderDetailsReorderLoadCart, emitReorderAddToCart);
addToChainAfter(orderHistoryReorderChain, OrderHistoryReorderLoadCart, emitReorderAddToCart);
addToChainAfter(orderStatusReorderChain, OrderStatusReorderLoadCart, emitReorderAddToCart);

// 5. OrderUpload (Products & VMI Bins), Requisitions, and SavedOrderDetails PlaceOrder
const emitCollectionAddToCart = (props: any) => {
    const state = props.getState();
    const lines = props.apiParameter?.cartLineCollection?.cartLines ?? props.apiResult?.cartLines;
    lines?.forEach((line: any) => {
        if (line.productId && line.qtyOrdered) {
            sendAddToCartEvent({ productId: line.productId, qtyOrdered: line.qtyOrdered }, state);
        }
    });
};
addToChainAfter(orderUploadProductsChain, OrderUploadProductsSendDataToApi, emitCollectionAddToCart);
addToChainAfter(orderUploadVmiBinsChain, OrderUploadVmiBinsSendDataToApi, emitCollectionAddToCart);
addToChainAfter(requisitionsAddLinesToCartChain, RequisitionsRequestDataFromApi, emitCollectionAddToCart);
addToChainAfter(savedOrderPlaceOrderChain, SavedOrderSendDataToApi, emitCollectionAddToCart);

// 6. ProductSelector: track last search query & attributionToken in module state for SetProduct
let lastProductSelectorSearch: {
    query?: string;
    attributionToken?: string | null;
} = {};

replaceInChain(searchProductsChain, SearchProductsRequestDataFromApi, async props => {
    if (props.parameter.query.length > 0) {
        const response = await autocompleteSearch(props.apiParameter);
        props.apiResult = response.products;
        /* Store Custom Code - Start */
        lastProductSelectorSearch = {
            query: props.parameter.query,
            attributionToken: response.attributionToken,
        };
        /* Store Custom Code - End */
    } else {
        /* Store Custom Code - Start */
        lastProductSelectorSearch = {};
        /* Store Custom Code - End */
    }
});

addToChainAfter(setProductChain, DispatchBeginSetProduct, props => {
    if (props.parameter.productId) {
        const state = props.getState();
        const searchResults = state.components.productSelector.searchResults;
        if (searchResults && lastProductSelectorSearch.query !== undefined) {
            const searchPosition = searchResults.findIndex(o => o.id === props.parameter.productId);
            if (searchPosition >= 0) {
                sendAutocompleteEvent(
                    {
                        searchQuery: lastProductSelectorSearch.query,
                        searchPosition,
                        attributionToken: lastProductSelectorSearch.attributionToken || undefined,
                    },
                    props.getState,
                );
            }
        }
        lastProductSelectorSearch = {};
    }
});

addToChainAfter(clearProductChain, ClearProductDispatchCompleteSetProduct, () => {
    lastProductSelectorSearch = {};
});

// 7. SendOdpEvents: ensure SearchNavigationEvent falls back to getCategoryById and does not drop the event
replaceInChain(sendProductListOdpEventsChain, ProductListSendOdpEvents, async props => {
    const state = props.getState();
    const apiParameter = state.pages.productList.parameter;
    if (!apiParameter) {
        return;
    }

    const unfilteredApiParameter = state.pages.productList.unfilteredApiParameter;
    const lastParameter = props.parameter.lastParameter;

    const odpEvent = createEvent("navigation", state);
    odpEvent.properties["action"] = "search";

    const productsDataView = getProductsDataView(state, apiParameter);
    const products = productsDataView?.value;
    if (products) {
        odpEvent.properties["search_product_details"] = JSON.stringify(
            products.map(o => ({
                product_id: o.id,
            })),
        );
    }

    odpEvent.properties["search_term"] = apiParameter.search ?? "";

    const searchFilters: string[] = [];
    if (odpEvent.properties["category"]) {
        searchFilters.push(`categories: ANY("${odpEvent.properties["category"]}")`);
    }

    let categoryId = apiParameter.categoryId;
    if (categoryId) {
        let categoryDisplay = "";
        while (categoryId) {
            let category: Readonly<Category> | undefined = getCategoryState(props.getState(), categoryId)?.value;
            if (!category) {
                await sleep(1000);
                category = getCategoryState(props.getState(), categoryId)?.value;
                /* Store Custom Code - Start */
                // Base SearchNavigationEvent did `if (!category) { return; }`, dropping the entire search ODP event
                // whenever a filtered categoryId wasn't loaded in state.data.categories.byId.
                if (!category) {
                    try {
                        category = await getCategoryById({ id: categoryId });
                    } catch {
                        break;
                    }
                }
            }
            if (!category) {
                break;
            }
            /* Store Custom Code - End */
            categoryDisplay = ` > ${category.shortDescription}${categoryDisplay}`;
            if (category.parentId === null) {
                break;
            }
            categoryId = category.parentId;
        }

        if (categoryDisplay.length > 0) {
            searchFilters.push(`categories: ANY("${categoryDisplay.substring(3)}")`);
            odpEvent.properties["category"] = categoryDisplay.substring(3);
        }
    }

    if (apiParameter.brandIds && apiParameter.brandIds.length > 0) {
        searchFilters.push(`attributes.brandId: ANY("${apiParameter.brandIds.join('", "')}")`);
    }

    if (apiParameter.productLineIds && apiParameter.productLineIds.length > 0) {
        searchFilters.push(`attributes.productLineId: ANY("${apiParameter.productLineIds.join('", "')}")`);
    }

    if (productsDataView && productsDataView.value) {
        odpEvent.properties["search_attribution_token"] = productsDataView.attributionToken;

        if (apiParameter.attributeValueIds && apiParameter.attributeValueIds.length > 0) {
            const dataByAttributeValueId: Dictionary<{ attributeName: string; value: string }> = {};
            for (const attributeType of getMergedAttributeTypeFacets(state, apiParameter, unfilteredApiParameter) ??
                []) {
                for (const attributeValue of attributeType.attributeValueFacets ?? []) {
                    dataByAttributeValueId[attributeValue.attributeValueId] = {
                        attributeName: attributeType.name,
                        value: attributeValue.value,
                    };
                }
            }

            const attributes: Dictionary<string[]> = {};
            for (const attributeValueId of apiParameter.attributeValueIds) {
                const data = dataByAttributeValueId[attributeValueId];
                if (!data) {
                    continue;
                }
                if (!attributes[data.attributeName]) {
                    attributes[data.attributeName] = [];
                }
                attributes[data.attributeName].push(data.value);
            }

            for (const attribute in attributes) {
                searchFilters.push(`attributes.${attribute}: ANY("${attributes[attribute].join('", "')}")`);
            }
        }
    }

    odpEvent.properties["search_filter"] = searchFilters.join(" AND ");

    if (productsDataView?.value && productsDataView.pagination) {
        odpEvent.properties["search_offset"] =
            (productsDataView.pagination.page - 1) * productsDataView.pagination.pageSize;

        const sort = productsDataView.pagination.sortType;
        const sendSort =
            sort === "2"
                ? "title"
                : sort === "3"
                  ? "title desc"
                  : sort === "4"
                    ? "price,title"
                    : sort === "5"
                      ? "price desc,title"
                      : sort === "9"
                        ? "brands,title"
                        : sort === "10"
                          ? "brands desc,title"
                          : "";

        if (sendSort !== "") {
            odpEvent.properties["search_order_by"] = sendSort;
        }
    }

    sendEvent(odpEvent, state);

    if (lastParameter) {
        if (apiParameter.sort !== lastParameter.sort) {
            const sortEvent = cloneDeep(odpEvent);
            sortEvent.properties["action"] = "sort";
            sendEvent(sortEvent, state);
        }

        if (
            apiParameter.categoryId !== lastParameter.categoryId ||
            !isEqual(apiParameter.productLineIds, lastParameter.productLineIds) ||
            !isEqual(apiParameter.brandIds, lastParameter.brandIds) ||
            !isEqual(apiParameter.attributeValueIds, lastParameter.attributeValueIds)
        ) {
            const filterEvent = cloneDeep(odpEvent);
            filterEvent.properties["action"] = "filter";
            sendEvent(filterEvent, state);
        }
    }

    if (props.parameter.catalogPage) {
        const page = getCurrentPage(state);
        sendCategoryPageViewEvent(page, props.parameter.catalogPage, apiParameter, state);
    }
});
